import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { RemoveInteractionReasonParams } from '@base/models/requests/cancel-meeting.req';
import { MeetService } from '@base/services/private/meet.service';
import { ModalService } from '@base/services/private/modal.service';
import { ModalHeaderComponent } from '@base/components/private/modals/modal-header/modal-header.component';
import { ModalBodyComponent } from '@base/components/private/modals/modal-body/modal-body.component';
import { FormsModule } from '@angular/forms';
import { RequestLabelService } from '@base/services/private/request-label.service';
import { ReqLabelParams } from '@base/models/requests/req-label.req';
import { TypeOfOperation, TypeOfRequestLabel } from '@base/enums/request-label.enum';
import { TranslatePipe } from '@base/pipes/private/translate.pipe';
import { finalize, Subscription } from 'rxjs';
import { GlobalService } from '@base/services/private/global.service';
import { StatusResponse } from '@base/enums/status-response.enum';
import { ModalFooterComponent } from '@base/components/private/modals/modal-footer/modal-footer.component';
import { SkeletonGeneralListComponent } from '@base/components/private/skeleton/skeleton-general-list/skeleton-general-list.component';
import { ThemeService } from '@base/services/private/theme.service';
import { GlobalStateService } from '@base/services/private/global-state.service';
import { TypeOfDecline } from '@base/enums/common.enum';

// local enum for active button states
enum ActiveButton {
  DECLINE_MEETING = 'decline-meeting',
}
@Component({
  selector: 'app-modal-decline-request',
  imports: [
    ModalHeaderComponent,
    ModalBodyComponent,
    FormsModule,
    forwardRef(() => TranslatePipe),
    ModalFooterComponent,
    SkeletonGeneralListComponent,
  ],
  providers: [forwardRef(() => TranslatePipe)],
  templateUrl: './modal-decline-request.component.html',
  styleUrl: './modal-decline-request.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalDeclineRequestComponent implements OnInit, OnDestroy {
  private readonly modalSrv = inject(ModalService);
  private readonly meetSrv = inject(MeetService);
  private readonly requestLabelSrv = inject(RequestLabelService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly globalSrv = inject(GlobalService);
  private readonly themeSrv = inject(ThemeService);
  private readonly globalStateSrv = inject(GlobalStateService);
  private readonly translatePipe = inject(TranslatePipe);

  @Input() peopleId = '';
  @Input() modalId = '';
  @Input() action: TypeOfRequestLabel | string = '';

  reasonSelected = 0;
  otherReason = '';
  reasons: string[] = [];

  isBgDarkMode = this.themeSrv.isBgDarkMode;
  isShowPlaceholder = true;
  isHttpRequestRunning = false;
  isExternalMode = this.globalStateSrv.isExternalMode();

  httpRequestObserver$: Subscription | null = null;
  errorMessage = '';

  ActiveButton = ActiveButton;
  activeButton = '';

  ngOnInit(): void {
    this.getReason();
    this.listenHttpRequestState();
  }

  ngOnDestroy(): void {
    this.httpRequestObserver$?.unsubscribe();
    this.isHttpRequestRunning = false;
    this.globalSrv.destroyModalRef(this.modalId);
  }

  /**
   * Listens for HTTP request state changes and updates the component state accordingly.
   * @returns void
   */
  listenHttpRequestState(): void {
    this.httpRequestObserver$ = this.globalSrv.httpRequestObserver().subscribe({
      next: (res) => {
        this.isHttpRequestRunning = res;
        this.triggerChangeDetection();
      },
    });
  }

  /**
   * Fetches the list of reasons for declining a request.
   * Updates the reasons array and sets the default selected reason.
   * Triggers change detection after data is loaded.
   * @returns void
   */
  getReason(): void {
    const params: RemoveInteractionReasonParams = {
      id: this.peopleId,
      type: 1,
    };
    this.meetSrv.getRemoveInteractionReason(params).subscribe({
      next: (res) => {
        this.reasons = res;
        if (res.length > 0) {
          this.reasonSelected = 0;
        }
        this.isShowPlaceholder = false;
        this.triggerChangeDetection();
      },
      error: (err) => {
        console.error('Error fetching decline request reasons:', err);
      },
    });
  }

  /**
   * Closes the modal with the provided modalId.
   * @returns void
   */
  close(): void {
    this.modalSrv.close();
  }

  /**
   * Shows a modal warning if the account is a preview account and status response is -2.
   */
  showModalWarning(): void {
    this.modalSrv.showModalWarningForPreviewAccount();
    return;
  }

  /**
   * Sends a decline request for the current person.
   * Builds the request parameters including reason and updates the label via RequestLabelService.
   * Closes either all modals or the current one based on closeAll flag, and triggers event update.
   * @returns void
   */
  declineRequest(): void {
    this.errorMessage = '';

    if (this.otherReason.trim().length > 100) {
      return;
    }
    const params: ReqLabelParams = {
      id_object: this.peopleId,
      action: TypeOfRequestLabel.DECLINE_HANDSHAKE,
      operation: TypeOfOperation.POSITIVE,
      reason: this.otherReason.trim().length > 0 ? -1 : this.reasonSelected,
      reason_text: this.otherReason.trim(),
    };
    this.activeButton = ActiveButton.DECLINE_MEETING;
    this.requestLabelSrv
      .updateLabel(params)
      .pipe(
        finalize(() => {
          this.activeButton = '';
        })
      )
      .subscribe({
        next: (res) => {
          if (res.status === StatusResponse.TEXT_SUCCESS) {
            if (this.isExternalMode) {
              this.globalSrv.postMessageToTargets({
                is_success: true,
                message: this.translatePipe.transform('MEETING_DECLINED'),
                description: this.translatePipe.transform('CLOSE_WINDOW'),
                isShowModal: true,
              });
              return;
            }

            this.close();
            const data = {
              status: true,
              data: {
                id: this.peopleId,
                action: this.action,
                newValue: {
                  declined: TypeOfDecline.USER_DECLINE,
                },
              },
            };
            this.requestLabelSrv.setEventUpdated(data);
          } else if (+res.status === StatusResponse.PREVIEW_ACCOUNT) {
            // Preview account returns status -2, but success returns 'SUCCESS'; always convert to number to check for -2.
            this.showModalWarning();
          }
        },
        error: (err) => {
          console.error('Error declining request:', err);
          this.errorMessage = 'SOMETHING_WENT_WRONG';
        },
      });
  }

  /**
   * Triggers change detection for the component.
   * @returns void
   */
  triggerChangeDetection() {
    this.changeDetectorRef.detectChanges();
    this.changeDetectorRef.markForCheck();
  }

  /**
   *
   * @param event - The change event from the select element.
   * Updates the selected reason based on user selection.
   * @returns void
   */
  onChangeReason(event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    this.reasonSelected = parseInt(selectElement.value, 10);
  }
}
